import type { AxiosRequestConfig, AxiosResponse } from "axios";
import type { IResponseData } from "@/shared/types/api";
import axios from "axios";
import qs from "qs";
import { getCurrentLocale } from "@/locales";
import { toast } from "vue-sonner";
import { FORBIDDEN_PATH, LOGIN_PATH } from "@/app/router/constants";
import { getApiBaseUrl } from "@/shared/config/env";
import { usePageTabsStore } from "@/shared/store/page-tabs";
import { useUserStore } from "@/entities/user/store";
import { BusinessRequestError, getBusinessErrorMessage } from "./request-errors";

export const ContentType = {
  Json: "application/json;charset=UTF-8",
  FormURLEncoded: "application/x-www-form-urlencoded;charset=UTF-8",
  FormData: "multipart/form-data;charset=UTF-8",
} as const;

const instance = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 60 * 1000,
  headers: { "Content-Type": ContentType.Json },
  paramsSerializer: (params) =>
    params instanceof URLSearchParams
      ? params.toString()
      : qs.stringify(params, { arrayFormat: "brackets" }),
});

function redirectToLogin() {
  if (window.location.pathname === LOGIN_PATH) {
    return;
  }

  const redirect = encodeURIComponent(`${window.location.pathname}${window.location.search}`);
  window.location.assign(`${LOGIN_PATH}?redirect=${redirect}`);
}

function redirectToForbidden() {
  if (window.location.pathname !== FORBIDDEN_PATH) {
    window.location.assign(FORBIDDEN_PATH);
  }
}

function showErrorToast(errorData?: Partial<IResponseData>, fallback = "请求失败") {
  const message = errorData ? getBusinessErrorMessage(errorData) : fallback;
  const description = errorData?.description;

  toast.error(message, {
    description: description && description !== message ? description : undefined,
  });
}

instance.interceptors.request.use((config) => {
  const userStore = useUserStore();

  if (userStore.token) {
    config.headers.Authorization = `Bearer ${userStore.token}`;
  }

  if (userStore.customerId) {
    config.headers.customerId = userStore.customerId;
  }

  config.headers.lang = getCurrentLocale();

  return config;
});

instance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;

    if (status === 401) {
      const userStore = useUserStore();
      userStore.logout();
      usePageTabsStore().clearTabs();
      toast.error("登录状态已失效，请重新登录");
      redirectToLogin();
      return Promise.reject(error);
    }

    if (status === 403) {
      showErrorToast(error?.response?.data, "暂无权限访问该资源");
      redirectToForbidden();
      return Promise.reject(error);
    }

    showErrorToast(error?.response?.data, error?.message || "请求失败");
    return Promise.reject(error);
  },
);

async function transformResponse<T, E>(response: AxiosResponse<IResponseData<T, E>>) {
  const { data } = response;

  if (data.code !== "success") {
    const error = new BusinessRequestError(data);
    error.notify();
    return Promise.reject(error);
  }

  return data;
}

const request = {
  async get<T = unknown, E = unknown>(url: string, config?: AxiosRequestConfig) {
    return transformResponse<T, E>(await instance.get(url, config));
  },

  async post<T = unknown, E = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) {
    return transformResponse<T, E>(await instance.post(url, data, config));
  },

  async put<T = unknown, E = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) {
    return transformResponse<T, E>(await instance.put(url, data, config));
  },

  async delete<T = unknown, E = unknown>(url: string, config?: AxiosRequestConfig) {
    return transformResponse<T, E>(await instance.delete(url, config));
  },

  async patch<T = unknown, E = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) {
    return transformResponse<T, E>(await instance.patch(url, data, config));
  },

  async upload<T = unknown>(key: string, file: File, config: AxiosRequestConfig) {
    const formData = config.data instanceof FormData ? config.data : new FormData();
    formData.append(key, file);

    const response = await instance.request<IResponseData<T>>({
      ...config,
      method: "POST",
      headers: { "Content-Type": ContentType.FormData },
      data: formData,
    });

    return transformResponse<T, unknown>(response);
  },
};

export default request;
